<template>
  <div class="column q-gutter-md">
    <q-card>
      <q-card-section class="text-h6">{{ t('title') }}</q-card-section>
      <q-separator />
      <q-card-section class="row q-col-gutter-md">
        <div class="col-12 col-md-6">
          <q-toggle
            v-model="settings.stock.allow_negative"
            :label="t('allowNegative')" />
        </div>
        <div class="col-12 col-md-6">
          <q-select
            v-model="settings.stock.consume_policy"
            :options="consumePolicies"
            :label="t('consumePolicy')"
            emit-value
            map-options
            dense />
        </div>
        <div class="col-12 col-md-4">
          <q-input
            v-model.number="settings.leasing.default_billing_interval_months"
            type="number"
            :label="t('billingInterval')"
            dense />
        </div>
        <div class="col-12 col-md-4">
          <q-input
            v-model.number="settings.leasing.default_inspection_interval_months"
            type="number"
            :label="t('inspectionInterval')"
            dense />
        </div>
        <div class="col-12 col-md-4">
          <q-input
            v-model.number="settings.leasing.billing_anchor_day"
            type="number"
            :label="t('anchorDay')"
            dense />
        </div>
        <div class="col-12 col-md-6">
          <q-select
            v-model="settings.leasing.proration_policy"
            :options="prorationPolicies"
            :label="t('proration')"
            emit-value
            map-options
            dense />
        </div>
        <div class="col-12 col-md-6">
          <q-input
            v-model="settings.leasing.invoice_memo"
            type="text"
            :label="t('memo')"
            dense />
        </div>
        <div class="col-12 col-md-4">
          <q-input
            v-model.number="settings.leasing.grace_period_days"
            type="number"
            :label="t('grace')"
            dense />
        </div>
      </q-card-section>
      <q-separator />
      <q-card-actions align="right">
        <q-btn
          v-if="canSave"
          color="primary"
          :label="t('save')"
          :loading="saving"
          @click="save" />
      </q-card-actions>
    </q-card>
  </div>
</template>

<script setup lang="ts">
import { axiosApi } from 'src/CrudManager/Core/Utils/axios'
import { computed, onMounted, ref } from 'vue'
import { useUserStore } from 'src/CrudManager/Core/stores/useUserStore'
import { showErrorNotification, showSuccessNotification } from 'src/CrudManager/Core/Utils/ToastNotification'
import { useI18n } from 'vue-i18n'

interface SystemSettingsState {
  stock: {
    allow_negative: boolean
    consume_policy: string
  }
  leasing: {
    default_billing_interval_months: number
    default_inspection_interval_months: number
    billing_anchor_day: number
    proration_policy: string
    invoice_memo: string
    grace_period_days: number
  }
}

const userStore = useUserStore()
const canSave = computed(() => userStore.hasPermission('cm-system_setting_update'))

const consumePolicies = [
  { label: 'FIFO', value: 'fifo' },
  { label: 'LIFO', value: 'lifo' },
  { label: 'FEFO', value: 'fefo' },
]
const prorationPolicies = computed(() => [
  { label: t('none'), value: 'none' },
  { label: t('daily'), value: 'daily' },
  { label: t('monthly'), value: 'monthly' },
])

const settings = ref<SystemSettingsState>({
  stock: { allow_negative: false, consume_policy: 'fifo' },
  leasing: {
    default_billing_interval_months: 3,
    default_inspection_interval_months: 3,
    billing_anchor_day: 1,
    proration_policy: 'none',
    invoice_memo: '',
    grace_period_days: 0,
  },
})

const saving = ref(false)

const { t } = useI18n({
  useScope: 'local',
  messages: {
    en: {
      title: 'System Settings',
      none: 'None',
      daily: 'Daily',
      monthly: 'Monthly',
      allowNegative: 'Allow negative stock',
      consumePolicy: 'Stock consume policy',
      billingInterval: 'Default billing interval (months)',
      inspectionInterval: 'Default inspection interval (months)',
      anchorDay: 'Billing anchor day',
      proration: 'Proration policy',
      memo: 'Default invoice memo',
      grace: 'Grace period days',
      save: 'Save',
    },
    fr: {
      title: 'Paramètres système',
      none: 'Aucun',
      daily: 'Quotidien',
      monthly: 'Mensuel',
      allowNegative: 'Autoriser le stock négatif',
      consumePolicy: 'Ordre de consommation du stock',
      billingInterval: 'Intervalle de facturation (mois)',
      inspectionInterval: 'Intervalle d’inspection (mois)',
      anchorDay: 'Jour de facturation',
      proration: 'Calcul au prorata',
      memo: 'Note de facture par défaut',
      grace: 'Délai de grâce (jours)',
      save: 'Enregistrer',
    },
  },
})
onMounted(async () => {
  try {
    settings.value = (await axiosApi.get('/admin/system-settings')).data
  } catch (error) {
    showErrorNotification(t('crudmanager.errors.unknown'), error)
  }
})

async function save() {
  try {
    saving.value = true
    settings.value = (await axiosApi.post('/admin/system-settings', settings.value)).data
    showSuccessNotification(t('common.success'))
  } catch (error) {
    showErrorNotification(t('crudmanager.errors.unknown'), error)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped></style>
